import type { RefObject } from "react"
import { useCallback, useRef, type FocusEvent } from "react"

import type { BusinessPartnerRepositorySuggestionResponseDto } from "~/gen/api/types/BusinessPartnerRepositorySuggestionResponseDto.ts"
import { MIN_BP_REPOSITORY_AUTOCOMPLETE_CHARACTERS } from "~/pages/Admin/components/Fields/BusinessPartnerRepositoryAutocomplete/utils.ts"

type ComboboxCommitKind = "none" | "create-new" | "suggestion"

export type UseBusinessPartnerRepositoryAutocompleteIdleBlurParams = {
  readonly linkedRepositoryCompanyNameRef: RefObject<string | null>
  readonly onOptionSubmit: (
    suggestion: BusinessPartnerRepositorySuggestionResponseDto
  ) => void | Promise<void>
  readonly onAutocompleteClear: () => void
  readonly onCreateNewWithoutDirectorySelection: (
    chosenName: string,
    duplicateCandidateId: string | null
  ) => void
  readonly organizationNameInputRef: RefObject<HTMLInputElement | null>
  /** Primary visible strong directory suggestion for `search` (name-ranked), or `null`. */
  readonly findFirstStrongSuggestion: (
    remoteOptions: readonly unknown[],
    search: string
  ) => BusinessPartnerRepositorySuggestionResponseDto | null
  /** Non-null trimmed name when `option` is the synthetic “create new” row. */
  readonly getCreateNewChosenName: (option: unknown) => string | null
  /** Directory suggestion payload when `option` is a repository row. */
  readonly getCommittedSuggestionIfAny: (
    option: unknown
  ) => BusinessPartnerRepositorySuggestionResponseDto | null
}

export type UseBusinessPartnerRepositoryAutocompleteIdleBlurReturn = {
  readonly outsideSubmitSyncRef: RefObject<((label: string) => void) | null>
  readonly handleAutocompleteFocus: (
    event: FocusEvent<HTMLInputElement>
  ) => void
  readonly handleAutocompleteClear: () => void
  readonly handleComboboxIdleBlur: (args: {
    searchValue: string
    remoteOptions: readonly unknown[]
  }) => void
  readonly onOptionSelected: (
    option: unknown,
    context: { readonly remoteOptions: readonly unknown[] }
  ) => void
}

export const useBusinessPartnerRepositoryAutocompleteIdleBlur = ({
  linkedRepositoryCompanyNameRef,
  onOptionSubmit,
  onAutocompleteClear,
  onCreateNewWithoutDirectorySelection,
  organizationNameInputRef,
  findFirstStrongSuggestion,
  getCreateNewChosenName,
  getCommittedSuggestionIfAny,
}: UseBusinessPartnerRepositoryAutocompleteIdleBlurParams): UseBusinessPartnerRepositoryAutocompleteIdleBlurReturn => {
  const outsideSubmitSyncRef = useRef<((label: string) => void) | null>(null)
  const comboboxCommitKindRef = useRef<ComboboxCommitKind>("none")

  const handleAutocompleteFocus = useCallback(
    (_event: FocusEvent<HTMLInputElement>): void => {
      comboboxCommitKindRef.current = "none"
    },
    []
  )

  const handleAutocompleteClear = useCallback(() => {
    comboboxCommitKindRef.current = "none"
    onAutocompleteClear()
  }, [onAutocompleteClear])

  const handleComboboxIdleBlur = useCallback(
    ({
      searchValue,
      remoteOptions,
    }: {
      searchValue: string
      remoteOptions: readonly unknown[]
    }): void => {
      if (comboboxCommitKindRef.current !== "none") {
        return
      }
      const trimmed = searchValue.trim()
      if (trimmed.length < MIN_BP_REPOSITORY_AUTOCOMPLETE_CHARACTERS) {
        return
      }
      const firstStrongSuggestion = findFirstStrongSuggestion(
        remoteOptions,
        trimmed
      )
      if (firstStrongSuggestion === null) {
        return
      }
      if (
        linkedRepositoryCompanyNameRef.current !== null &&
        linkedRepositoryCompanyNameRef.current === firstStrongSuggestion.name
      ) {
        return
      }
      comboboxCommitKindRef.current = "suggestion"
      outsideSubmitSyncRef.current?.(firstStrongSuggestion.name)
      void onOptionSubmit(firstStrongSuggestion)
    },
    [findFirstStrongSuggestion, linkedRepositoryCompanyNameRef, onOptionSubmit]
  )

  const onOptionSelected = useCallback(
    (
      option: unknown,
      context: { readonly remoteOptions: readonly unknown[] }
    ): void => {
      const createNewChosenName = getCreateNewChosenName(option)
      if (createNewChosenName !== null) {
        comboboxCommitKindRef.current = "create-new"
        const firstStrong = findFirstStrongSuggestion(
          context.remoteOptions,
          createNewChosenName
        )
        const duplicateCandidateId =
          firstStrong !== null ? firstStrong.repositoryRecordId : null
        onCreateNewWithoutDirectorySelection(
          createNewChosenName,
          duplicateCandidateId
        )
        queueMicrotask(() => {
          organizationNameInputRef.current?.blur()
        })
        return
      }
      const suggestion = getCommittedSuggestionIfAny(option)
      if (suggestion === null) {
        return
      }
      comboboxCommitKindRef.current = "suggestion"
      void onOptionSubmit(suggestion)
    },
    [
      findFirstStrongSuggestion,
      getCommittedSuggestionIfAny,
      getCreateNewChosenName,
      onCreateNewWithoutDirectorySelection,
      onOptionSubmit,
      organizationNameInputRef,
    ]
  )

  return {
    outsideSubmitSyncRef,
    handleAutocompleteFocus,
    handleAutocompleteClear,
    handleComboboxIdleBlur,
    onOptionSelected,
  }
}
